// frontend/src/pages/Activity.tsx
// Shows the user's recent file interactions stored in localStorage.

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Button,
  Chip,
  Divider,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  DeleteSweep as ClearIcon,
  VideoFile as VideoIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  TableChart as CsvIcon,
  TextSnippet as TextIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getActivity,
  removeActivity,
  clearActivity,
  ActivityEntry,
  ActivityAction,
} from '../services/activityService';
import { FileType } from '../services/api';

const rocheBlue = '#0B41CD';

// ── helpers ──────────────────────────────────────────────────────────────────

function actionLabel(action: ActivityAction): string {
  switch (action) {
    case 'played':     return 'Played';
    case 'viewed':     return 'Viewed';
    case 'downloaded': return 'Downloaded';
    case 'searched':   return 'Searched';
  }
}

function actionColor(action: ActivityAction): 'primary' | 'success' | 'warning' | 'default' {
  switch (action) {
    case 'played':     return 'primary';
    case 'viewed':     return 'success';
    case 'downloaded': return 'warning';
    case 'searched':   return 'default';
  }
}

function ActionIcon({ action }: { action: ActivityAction }) {
  switch (action) {
    case 'played':     return <PlayIcon fontSize="small" />;
    case 'viewed':     return <ViewIcon fontSize="small" />;
    case 'downloaded': return <DownloadIcon fontSize="small" />;
    case 'searched':   return <SearchIcon fontSize="small" />;
  }
}

function FileAvatar({ fileType }: { fileType: FileType | null }) {
  const sx = { bgcolor: 'rgba(11, 65, 205, 0.08)', color: rocheBlue };
  switch (fileType) {
    case FileType.VIDEO:    return <Avatar sx={sx}><VideoIcon /></Avatar>;
    case FileType.IMAGE:    return <Avatar sx={sx}><ImageIcon /></Avatar>;
    case FileType.CSV:      return <Avatar sx={sx}><CsvIcon /></Avatar>;
    case FileType.TEXT:     return <Avatar sx={sx}><TextIcon /></Avatar>;
    default:                return <Avatar sx={sx}><FileIcon /></Avatar>;
  }
}

function formatRelativeTime(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins  = Math.floor(diff / 60_000);
    const hours = Math.floor(diff / 3_600_000);
    const days  = Math.floor(diff / 86_400_000);
    if (mins  < 1)  return 'Just now';
    if (mins  < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days  < 7)  return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

// ── component ─────────────────────────────────────────────────────────────────

const Activity: React.FC = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [confirmClear, setConfirmClear] = useState(false);

  const refresh = useCallback(() => setEntries(getActivity()), []);

  useEffect(() => {
    refresh();
    // Re-read when the tab regains focus (user may have acted in another tab)
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const handleRemove = (id: string) => {
    removeActivity(id);
    refresh();
  };

  const handleClear = () => {
    clearActivity();
    setEntries([]);
    setConfirmClear(false);
  };

  const handleOpen = (entry: ActivityEntry) => {
    if (entry.action === 'searched') {
      navigate(`/search?q=${encodeURIComponent(entry.key)}`);
      return;
    }
    const ext = entry.key.split('.').pop()?.toLowerCase() ?? '';
    if (['mp4', 'mov', 'webm', 'avi', 'mkv'].includes(ext)) {
      navigate(`/play/${encodeURIComponent(entry.key)}`);
    } else if (ext === 'csv') {
      navigate(`/view/csv/${encodeURIComponent(entry.key)}`);
    } else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
      navigate(`/view/image/${encodeURIComponent(entry.key)}`);
    } else {
      navigate(`/view/text/${encodeURIComponent(entry.key)}`);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HistoryIcon sx={{ color: rocheBlue, fontSize: 28 }} />
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Activity
          </Typography>
          {entries.length > 0 && (
            <Chip
              label={entries.length}
              size="small"
              sx={{ bgcolor: rocheBlue, color: 'white', fontWeight: 600 }}
            />
          )}
        </Box>
        {entries.length > 0 && (
          <Tooltip title="Clear all activity">
            <Button
              startIcon={<ClearIcon />}
              onClick={() => setConfirmClear(true)}
              color="inherit"
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              Clear all
            </Button>
          </Tooltip>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Files and searches you've interacted with in this browser. Stored locally — visible only to you.
      </Typography>

      {/* Empty state */}
      {entries.length === 0 ? (
        <Alert
          severity="info"
          icon={<HistoryIcon />}
          sx={{ borderRadius: 2 }}
        >
          No activity yet. Play, view, or download a file and it will appear here.
        </Alert>
      ) : (
        <Paper elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2, overflow: 'hidden' }}>
          <List disablePadding>
            {entries.map((entry, idx) => (
              <React.Fragment key={entry.id}>
                {idx > 0 && <Divider component="li" />}
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: 'rgba(11, 65, 205, 0.04)' },
                    pr: 8,
                  }}
                  onClick={() => handleOpen(entry)}
                >
                  <ListItemAvatar>
                    <FileAvatar fileType={entry.fileType} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, wordBreak: 'break-all' }}
                        >
                          {entry.displayName}
                        </Typography>
                        <Chip
                          icon={<ActionIcon action={entry.action} />}
                          label={actionLabel(entry.action)}
                          size="small"
                          color={actionColor(entry.action)}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {formatRelativeTime(entry.timestamp)}
                        {entry.action !== 'searched' && (
                          <> · <span style={{ opacity: 0.6, fontSize: '0.7rem' }}>{entry.key}</span></>
                        )}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Remove from history">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleRemove(entry.id); }}
                        sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Confirm clear dialog */}
      <Dialog open={confirmClear} onClose={() => setConfirmClear(false)}>
        <DialogTitle>Clear activity history?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently remove all {entries.length} entries from your local activity history.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClear(false)}>Cancel</Button>
          <Button onClick={handleClear} color="error" variant="contained">Clear all</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Activity;
